import { HealthSex, HealthWeightIntent, type HealthActivityLevel } from './healthCalorieGoalCalc';
import { normalizeNutrientValues, NutrientKey, roundNutrientAmount, type NutrientValue } from './nutrientCore';

export enum MacroStrategyId {
    BALANCED = 'BALANCED',
}

export const DEFAULT_MACRO_STRATEGY = MacroStrategyId.BALANCED;

export type NutrientGoalsInput = {
    kcalGoal: number;
    sex?: HealthSex | null;
    bodyWeightKg?: number | null;
    activityLevel?: HealthActivityLevel | null;
    weightIntent?: HealthWeightIntent | null;
    bodyFatPercent?: number | null;
    leanMassKg?: number | null;
};

export type MacroStrategyMeta = {
    id: MacroStrategyId;
    label: string;
};

export type NutrientGoalBand = {
    amount: number;
    min?: number;
    max?: number;
};

export type MacroStrategyMacroOutput = {
    macros: NutrientValue[];
    bands: Partial<Record<NutrientKey, NutrientGoalBand>>;
    ceilings: NutrientValue[];
};

type MacroStrategy = {
    meta: MacroStrategyMeta;
    calculateMacros: (ctx: NutrientGoalsInput) => MacroStrategyMacroOutput;
};

const KG_TO_LB = 2.2046226218;
const BALANCED_PROTEIN_LBM_LB_RATIO = 0.9;
const BALANCED_PROTEIN_LBM_LB_MIN = 0.85;
const BALANCED_PROTEIN_LBM_LB_MAX = 0.95;
const BALANCED_FAT_KCAL_RATIO = 0.25;
const BALANCED_FAT_KCAL_BAND_MIN = 0.2;
const BALANCED_FAT_KCAL_BAND_MAX = 0.3;
const BALANCED_FAT_G_PER_KG_MIN = 0.6;
const BALANCED_FAT_G_PER_KG_TARGET = 0.8;
const BALANCED_FAT_G_PER_KG_MAX = 1.0;
const INBODY_PROTEIN_G_PER_KG_LEAN_MIN = 1.6;
const INBODY_PROTEIN_G_PER_KG_LEAN_MAX = 2.2;

const BALANCED_SUGAR_KCAL_RATIO = 0.1;
const BALANCED_SATURATED_FAT_KCAL_RATIO = 0.1;

const DEFAULT_BODY_FAT_PERCENT: Record<HealthSex, number> = {
    [HealthSex.MALE]: 28,
    [HealthSex.FEMALE]: 28,
};
const FALLBACK_BODY_FAT_PERCENT = 28;

type MacroGrams = { proteinG: number; carbsG: number; fatG: number };

function leanMassLb(weightKg: number, bodyFatPercent: number): number {
    return weightKg * (1 - bodyFatPercent / 100) * KG_TO_LB;
}

function resolveBodyFatPercent(ctx: NutrientGoalsInput): number {
    const bf = ctx.bodyFatPercent;
    if (bf != null && Number.isFinite(bf) && bf > 0 && bf < 100) return bf;
    if (ctx.sex === HealthSex.MALE) return DEFAULT_BODY_FAT_PERCENT[HealthSex.MALE];
    if (ctx.sex === HealthSex.FEMALE) return DEFAULT_BODY_FAT_PERCENT[HealthSex.FEMALE];
    return FALLBACK_BODY_FAT_PERCENT;
}

function hasMeasuredLeanMass(ctx: NutrientGoalsInput): boolean {
    return ctx.leanMassKg != null && Number.isFinite(ctx.leanMassKg) && ctx.leanMassKg > 0;
}

function inBodyProteinGPerKgLean(intent?: HealthWeightIntent | null): { target: number; min: number; max: number } {
    const target = intent === HealthWeightIntent.LOSE ? 2.0 : intent === HealthWeightIntent.GAIN ? 1.6 : 1.8;
    return {
        target,
        min: INBODY_PROTEIN_G_PER_KG_LEAN_MIN,
        max: INBODY_PROTEIN_G_PER_KG_LEAN_MAX,
    };
}

function inBodyProteinFromLean(leanMassKg: number, intent?: HealthWeightIntent | null) {
    const ratio = inBodyProteinGPerKgLean(intent);
    return {
        amount: Math.round(leanMassKg * ratio.target),
        min: Math.round(leanMassKg * ratio.min),
        max: Math.round(leanMassKg * ratio.max),
    };
}

function inBodyFatFromWeight(kcal: number, proteinG: number, weightKg: number) {
    const min = Math.round(weightKg * BALANCED_FAT_G_PER_KG_MIN);
    const max = Math.round(weightKg * BALANCED_FAT_G_PER_KG_MAX);
    let amount = Math.round(weightKg * BALANCED_FAT_G_PER_KG_TARGET);
    const maxByKcal = Math.max(0, Math.round((kcal - proteinG * 4) / 9));
    amount = Math.min(amount, maxByKcal, max);
    amount = Math.max(amount, Math.min(min, maxByKcal));
    return { amount, min, max };
}

function balancedProteinG(weightKg: number, bodyFatPercent: number, lbmRatio = BALANCED_PROTEIN_LBM_LB_RATIO): number {
    return Math.round(lbmRatio * leanMassLb(weightKg, bodyFatPercent));
}

function balancedFatG(kcal: number, proteinG: number, fatKcalRatio = BALANCED_FAT_KCAL_RATIO): number {
    let fatG = Math.round((kcal * fatKcalRatio) / 9);
    const carbsG = Math.round((kcal - proteinG * 4 - fatG * 9) / 4);
    if (carbsG < 0) {
        fatG = Math.max(0, Math.round((kcal - proteinG * 4) / 9));
    }
    return fatG;
}

function balancedCarbsG(kcal: number, proteinG: number, fatG: number): number {
    return Math.max(0, Math.round((kcal - proteinG * 4 - fatG * 9) / 4));
}

function calculatorNetBalancedMacros(kcal: number, weightKg: number, bodyFatPercent: number): MacroGrams {
    const proteinG = balancedProteinG(weightKg, bodyFatPercent);
    const fatG = balancedFatG(kcal, proteinG);
    const carbsG = balancedCarbsG(kcal, proteinG, fatG);
    return { proteinG, fatG, carbsG };
}

function balancedMacroBands(
    kcal: number,
    weightKg: number,
    bodyFatPercent: number,
    macros: MacroGrams,
): Partial<Record<NutrientKey, NutrientGoalBand>> {
    const proteinMin = balancedProteinG(weightKg, bodyFatPercent, BALANCED_PROTEIN_LBM_LB_MIN);
    const proteinMax = balancedProteinG(weightKg, bodyFatPercent, BALANCED_PROTEIN_LBM_LB_MAX);
    const fatMin = balancedFatG(kcal, macros.proteinG, BALANCED_FAT_KCAL_BAND_MIN);
    const fatMax = balancedFatG(kcal, macros.proteinG, BALANCED_FAT_KCAL_BAND_MAX);

    const carbsAtHighProteinFat = {
        carbsG: balancedCarbsG(kcal, proteinMax, fatMax),
    };
    const carbsAtLowProteinFat = {
        carbsG: balancedCarbsG(kcal, proteinMin, fatMin),
    };

    return {
        [NutrientKey.PROTEIN]: { amount: macros.proteinG, min: proteinMin, max: proteinMax },
        [NutrientKey.CARBS]: {
            amount: macros.carbsG,
            min: Math.min(carbsAtHighProteinFat.carbsG, carbsAtLowProteinFat.carbsG),
            max: Math.max(carbsAtHighProteinFat.carbsG, carbsAtLowProteinFat.carbsG),
        },
        [NutrientKey.FAT]: { amount: macros.fatG, min: fatMin, max: fatMax },
    };
}

function balancedCeilings(kcal: number): NutrientValue[] {
    return normalizeNutrientValues([
        { key: NutrientKey.SUGAR, amount: roundNutrientAmount((kcal * BALANCED_SUGAR_KCAL_RATIO) / 4) },
        { key: NutrientKey.SATURATED_FAT, amount: roundNutrientAmount((kcal * BALANCED_SATURATED_FAT_KCAL_RATIO) / 9) },
    ]);
}

function defaultKcalSplitMacros(kcal: number): NutrientValue[] {
    if (kcal <= 0) return [];
    return normalizeNutrientValues([
        { key: NutrientKey.PROTEIN, amount: (kcal * 0.3) / 4 },
        { key: NutrientKey.CARBS, amount: (kcal * 0.4) / 4 },
        { key: NutrientKey.FAT, amount: (kcal * 0.3) / 9 },
    ]);
}

function calculateBalancedMacros(ctx: NutrientGoalsInput): MacroStrategyMacroOutput {
    const kcal = Math.max(0, Math.round(ctx.kcalGoal));
    const weight = ctx.bodyWeightKg;

    if (weight == null || !Number.isFinite(weight) || weight <= 0) {
        const fallback = defaultKcalSplitMacros(kcal);
        const proteinG = fallback.find(row => row.key === NutrientKey.PROTEIN)?.amount ?? 0;
        const carbsG = fallback.find(row => row.key === NutrientKey.CARBS)?.amount ?? 0;
        const fatG = fallback.find(row => row.key === NutrientKey.FAT)?.amount ?? 0;
        return {
            macros: fallback,
            bands: {
                [NutrientKey.PROTEIN]: { amount: proteinG },
                [NutrientKey.CARBS]: { amount: carbsG },
                [NutrientKey.FAT]: { amount: fatG },
            },
            ceilings: balancedCeilings(kcal),
        };
    }

    const measuredLean = hasMeasuredLeanMass(ctx);
    let proteinG: number;
    let fatG: number;
    let carbsG: number;
    let bands: Partial<Record<NutrientKey, NutrientGoalBand>>;

    if (measuredLean) {
        const protein = inBodyProteinFromLean(ctx.leanMassKg!, ctx.weightIntent);
        const fat = inBodyFatFromWeight(kcal, protein.amount, weight);
        proteinG = protein.amount;
        fatG = fat.amount;
        carbsG = balancedCarbsG(kcal, proteinG, fatG);
        bands = {
            [NutrientKey.PROTEIN]: { amount: proteinG, min: protein.min, max: protein.max },
            [NutrientKey.FAT]: { amount: fatG, min: fat.min, max: fat.max },
            [NutrientKey.CARBS]: {
                amount: carbsG,
                min: Math.max(0, balancedCarbsG(kcal, protein.max, fat.max)),
                max: Math.max(0, balancedCarbsG(kcal, protein.min, fat.min)),
            },
        };
    } else {
        const bodyFatPercent = resolveBodyFatPercent(ctx);
        const macros = calculatorNetBalancedMacros(kcal, weight, bodyFatPercent);
        proteinG = macros.proteinG;
        fatG = macros.fatG;
        carbsG = macros.carbsG;
        bands = balancedMacroBands(kcal, weight, bodyFatPercent, macros);
    }

    return {
        macros: normalizeNutrientValues([
            { key: NutrientKey.PROTEIN, amount: proteinG },
            { key: NutrientKey.CARBS, amount: carbsG },
            { key: NutrientKey.FAT, amount: fatG },
        ]),
        bands,
        ceilings: balancedCeilings(kcal),
    };
}

const BALANCED_STRATEGY: MacroStrategy = {
    meta: { id: MacroStrategyId.BALANCED, label: 'Balanced' },
    calculateMacros: calculateBalancedMacros,
};

const MACRO_STRATEGIES: Record<MacroStrategyId, MacroStrategy> = {
    [MacroStrategyId.BALANCED]: BALANCED_STRATEGY,
};

export function listMacroStrategies(): MacroStrategyMeta[] {
    return Object.values(MACRO_STRATEGIES).map(strategy => strategy.meta);
}

export function macroStrategyMeta(strategy: MacroStrategyId = DEFAULT_MACRO_STRATEGY): MacroStrategyMeta {
    return MACRO_STRATEGIES[strategy].meta;
}

export function isMacroStrategyId(value: string): value is MacroStrategyId {
    return (Object.values(MacroStrategyId) as string[]).includes(value);
}

export function calculateMacroStrategy(
    ctx: NutrientGoalsInput,
    strategy: MacroStrategyId = DEFAULT_MACRO_STRATEGY,
): MacroStrategyMacroOutput {
    return MACRO_STRATEGIES[strategy].calculateMacros(ctx);
}
